import React, {ReactElement, useRef, ReactNode, forwardRef, Ref, CSSProperties} from 'react';
import {Card, CardHeader} from '@mui/material';
import DataCardActions, {BulkStudentActions} from '../DataCardActions/DataCardActions';
import {DataService} from 'src/services/types';
import BlurredCardContent from '../BlurredCardContent/BlurredCardContent';
import {EducationPhase, LoadState} from 'src/types/enums';
import {Blurrable} from 'src/components/Table/types';
import classNames from 'classnames';
import {useSummedRefDimensions} from 'src/hooks/useSummedRefDimensions';
import {DEFAULT_CARD_CONTENT_HEIGHT} from 'src/components/Cards/utils';
import {CardHeading} from 'src/components/Cards/CardHeading';
import styles from './styles.module.scss';

export interface DataComponentProps<Type> {
  service: DataService<Type>;
  fullPage?: boolean;
}

type DataCardProps = Partial<BulkStudentActions> &
  Pick<CSSProperties, 'minHeight'> &
  Blurrable & {
    loadState: LoadState;
    children: ReactNode;
    title: string;
    actionsActive?: boolean;
    blurCardContent?: boolean;
    blurText?: string;
    cardContentRef?: Ref<HTMLDivElement>;
    elementId?: string;
    fullPage?: boolean;
    subheading?: string;
    subtitle?: string;
    tooltip?: ReactNode;
  };

function DataCard(props: DataCardProps, ref: Ref<HTMLDivElement>): ReactElement {
  const loading = props.loadState <= LoadState.LOADING && !props.fullPage;
  const cardHeaderRef = useRef(null);
  const cardHeaderDimensions = useSummedRefDimensions(cardHeaderRef);

  return (
    <Card
      id={props.elementId || ''}
      ref={ref}
      className={classNames({[styles.fullPage]: props.fullPage})}
      classes={{root: styles.card}}
    >
      <CardHeader
        ref={cardHeaderRef}
        disableTypography
        title={<CardHeading {...props} />}
        action={
          !loading && (
            <DataCardActions
              sendMessageToSelected={props.sendMessageToSelected}
              studentFiltersForSelected={props.studentFiltersForSelected}
              actionsActive={Boolean(props.actionsActive)}
              searchInput={props.searchInput}
              educationPhase={props.educationPhase || EducationPhase.HIGH_SCHOOL}
              downloadUrl={props.downloadUrl || ''}
            />
          )
        }
      />
      <BlurredCardContent
        fullPageTop={cardHeaderDimensions.height}
        blur={props.blur}
        blurText={props.blurText}
        loading={loading}
        minHeight={props.minHeight || DEFAULT_CARD_CONTENT_HEIGHT}
        ref={props.cardContentRef}
      >
        {props.subheading && <div className="shared-font body">{props.subheading}</div>}
        {props.children}
      </BlurredCardContent>
    </Card>
  );
}

export default forwardRef(DataCard);
